import type {
  GetRemediationScopeResponse,
  RemediationItemSummary,
  RemediationScopeGenerateItem,
  RemediationScopeRemediateItem,
} from '@libs/types';

import { RemediationItemType } from '@libs/constants';

/**
 * The scope minus whatever the run already holds.
 *
 * Offering a document the run already has is offering a no-op: the backend
 * dedupes under a row lock and drops it, so the user ticks a box, presses Add,
 * and is told nothing was added. Worse, it is indistinguishable from the
 * document they actually meant to add — so the fix belongs in the picker, not
 * in the response.
 *
 * Matched on the snapshotted document name within its own kind. The item
 * summary carries no source ids — only what the run recorded when it was scoped
 * — and `documentName` is exactly the field the append endpoint snapshots those
 * candidates into (`name` for a generated document, `fileName` for an upload).
 * So the name is the join, and the kind keeps the two lists from colliding on
 * it.
 *
 * A courtesy, not the guard: the backend still dedupes authoritatively, so a
 * miss here costs a candidate that quietly adds nothing rather than a duplicate
 * document.
 */
export function outstandingCandidates(
  scope: Pick<GetRemediationScopeResponse, 'generate' | 'remediate'> | undefined,
  items: RemediationItemSummary[],
): { generate: RemediationScopeGenerateItem[]; remediate: RemediationScopeRemediateItem[] } {
  const haveGenerate = new Set(
    items.filter((i) => i.type === RemediationItemType.GENERATE).map((i) => i.documentName),
  );
  const haveRemediate = new Set(
    items.filter((i) => i.type === RemediationItemType.REMEDIATE).map((i) => i.documentName),
  );

  return {
    generate: (scope?.generate ?? []).filter((g) => !haveGenerate.has(g.name)),
    remediate: (scope?.remediate ?? []).filter((r) => !haveRemediate.has(r.fileName)),
  };
}
